import React, { useState, useEffect } from 'react';
import { monthlyTimesheetAPI } from '../services/api';
import { MonthlyTimesheet } from '../types';
import TimesheetDetailModal from './TimesheetDetailModal';

const TimesheetApprovalQueue: React.FC = () => {
  const [timesheets, setTimesheets] = useState<MonthlyTimesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<number | null>(null);

  useEffect(() => {
    fetchPendingTimesheets();
  }, []);

  const fetchPendingTimesheets = async () => {
    try {
      setLoading(true);
      const response = await monthlyTimesheetAPI.getPendingApprovals();
      // Handle both paginated and non-paginated responses
      const data = response.data;
      if (Array.isArray(data)) {
        setTimesheets(data);
      } else if (data && Array.isArray(data.results)) {
        setTimesheets(data.results);
      } else {
        setTimesheets([]);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch pending timesheets:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load pending timesheets');
      setTimesheets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('Approve this timesheet?')) {
      return;
    }

    try {
      await monthlyTimesheetAPI.approve(id);
      alert('Timesheet approved successfully!');
      fetchPendingTimesheets();
    } catch (error: any) {
      console.error('Failed to approve timesheet:', error);
      alert(error.response?.data?.error || 'Failed to approve timesheet');
    }
  };

  const handleReject = async (id: number) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) {
      return;
    }

    try {
      await monthlyTimesheetAPI.reject(id, reason);
      alert('Timesheet rejected successfully!');
      fetchPendingTimesheets();
    } catch (error: any) {
      console.error('Failed to reject timesheet:', error);
      alert(error.response?.data?.error || 'Failed to reject timesheet');
    }
  };

  const formatMonth = (year: number, month: number) => {
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Timesheet Approval Queue</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h2>Timesheet Approval Queue</h2>
        
        {error && <div className="error">{error}</div>}
        
        {timesheets.length === 0 ? (
          <p style={{ color: '#7f8c8d', marginTop: '1rem' }}>
            No timesheets pending approval.
          </p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Month</th>
                  <th>Total Hours</th>
                  <th>Entries</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.map((timesheet) => (
                  <tr key={timesheet.id}>
                    <td>
                      <strong>{timesheet.user_full_name || timesheet.user_username}</strong>
                    </td>
                    <td>{formatMonth(timesheet.year, timesheet.month)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {parseFloat(timesheet.total_hours || '0').toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>{timesheet.entries_count || 0}</td>
                    <td>
                      {timesheet.submitted_at 
                        ? new Date(timesheet.submitted_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => setSelectedTimesheetId(timesheet.id!)}
                        className="btn btn-primary btn-small"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleApprove(timesheet.id!)}
                        className="btn btn-success btn-small"
                        style={{ backgroundColor: '#27ae60' }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(timesheet.id!)}
                        className="btn btn-danger btn-small"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedTimesheetId && (
        <TimesheetDetailModal
          timesheetId={selectedTimesheetId}
          onClose={() => setSelectedTimesheetId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onUpdate={fetchPendingTimesheets}
        />
      )}
    </div>
  );
};

export default TimesheetApprovalQueue;

